/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from "@angular/core";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {CommonState} from "@app/store/common/common.state";
import {DEFAULT_DIABETES_REPORT_DATA_TYPES} from "@doctor-panel-modules/patient-treatment-plan/models/diabetes-report-data-types";
import {PatientTreatmentPlanMonState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-mon.state";
import {environment} from "@env/environment";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {ClinicalDataType, TrecReportContext, TrecReportDiabetes} from "@trec/api/clinical-data";
import {StoreAction} from "@trec/components/store";
import {addDays, startOfDay} from "date-fns";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {TpMonActions} from "../../../store/patient-treatment-plan-mon.actions";

@Component({
  selector: "c3-config-reports",
  templateUrl: "./config-reports.component.html",
  styleUrls: ["./config-reports.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigReportsComponent extends ConfigSectionComponent implements OnInit, OnDestroy {
  // Data /////////////////////////////////////////////////////////////////////////////////////////

  @Select(PatientTreatmentPlanMonState.reportsConfig)
  reportsConfig$: Observable<TrecReportDiabetes | null>;
  @Select(hasActionsExecuting([TpMonActions.FetchReportsConfig, TpMonActions.UpdateReportsConfig]))
  loading$: Observable<boolean>;

  @Input()
  context: RegistryContexts[];

  // Periodicity //////////////////////////////////
  reportsConfig: TrecReportDiabetes | null = null;
  periodicity = 0;

  readonly CTXS = RegistryContexts;
  readonly CD_TYPE = ClinicalDataType;

  get dataTypes(): ClinicalDataType[] {
    return this.reportsConfig ? this.reportsConfig.data.data_types : this.defaultDataTypes;
  }

  private get defaultDataTypes(): ClinicalDataType[] {
    const patientPersonalData = this.store.selectSnapshot(CommonState.patientPersonalData);

    // Get default data types
    let reportDataTypes = [...DEFAULT_DIABETES_REPORT_DATA_TYPES];
    if (patientPersonalData && patientPersonalData.genotype === "Y") {
      // Remove observation/menstruation if patient is male
      reportDataTypes = reportDataTypes.filter(
        (cdType) => cdType !== ClinicalDataType.MENSTRUATION
      );
    }
    return reportDataTypes;
  }

  private valid = new BehaviorSubject(false);
  private touched = new BehaviorSubject(false);

  get touched$(): Observable<boolean> {
    return this.touched.asObservable();
  }

  get valid$(): Observable<boolean> {
    return this.valid.asObservable();
  }

  private subscr: Subscription;

  constructor(private store: Store, private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // Fetch config
    this.store.dispatch(new TpMonActions.FetchReportsConfig());

    // Get current config
    this.subscr = this.reportsConfig$.subscribe((reportsConfig) => {
      this.reportsConfig = reportsConfig;

      if (reportsConfig) {
        this.periodicity = reportsConfig.data.periodicity;
        this.valid.next(true);
      }

      this.cd.markForCheck();
    });
  }

  onPeriodicityChange() {
    this.touched.next(true);
    this.valid.next(true);
  }

  prepareSaveDataAction(): StoreAction | null {
    let report: TrecReportDiabetes;

    // First report not exists -> create it
    if (!this.reportsConfig) {
      const patientUserEntry = this.store.selectSnapshot(CommonState.patientUserEntry);
      const patientContext = this.store.selectSnapshot(CommonState.patientContext);
      const structureId = this.store.selectSnapshot(CommonState.currentStructureId);

      if (!(patientUserEntry && patientUserEntry[0].appPrescription)) {
        throw new Error(`Try to save new report config but patient user entry is missing`);
      }
      if (!patientContext) {
        throw new Error(`Try to save new report config but current context is missing`);
      }
      if (!structureId) {
        throw new Error(`Try to save new report config but structure id is missing`);
      }

      let reportContext: TrecReportContext = "diabetes";

      if (patientContext.includes(RegistryContexts.DIABETES1) || patientContext.includes(RegistryContexts.DIABETES2)) {
        reportContext = "diabetes";
      }
      if (patientContext.includes(RegistryContexts.DIABETES1) || patientContext.includes(RegistryContexts.DIABETES2)) {
        reportContext = "gestation";
      }

      const now = new Date();
      // Today at midnight // Why? Because it has been decided that the reasoner will produce the next reports at midnight
      const reportStartDate = startOfDay(now);

      const newReport = TrecReportDiabetes.create(
        // Prepare a new report
        {patientId: patientUserEntry[0].patientId},
        {
          start_date: reportStartDate.valueOf(),
          end_date: addDays(reportStartDate, this.periodicity).valueOf(),
          source: environment.appId,
          provenance: "medical",
        },
        {
          target_id: structureId,
          delivery_type: "EMAIL",
          data_types: this.defaultDataTypes,
          status: "unread",
          periodicity: this.periodicity,
          from_date: new Date().valueOf(),
          to_date: new Date(patientUserEntry[0].appPrescription.end).valueOf(),
          context: reportContext,
        }
      );

      report = newReport;
    } else {
      // Periodicity changed -> update report
      const updatedReport = this.reportsConfig.clone(
        {
          // Set the report start to start of day // Why? because it has been decided that all reports must start at midnight
          start_date: startOfDay(this.reportsConfig.metadata.start_date).valueOf(),
          end_date: startOfDay(this.reportsConfig.metadata.end_date).valueOf(),
        },
        {periodicity: this.periodicity}
      );
      report = updatedReport;
    }

    return new TpMonActions.UpdateReportsConfig({report});
  }

  resetTouched(): void {
    this.touched.next(false);
  }

  ngOnDestroy() {
    if (this.subscr) {
      this.subscr.unsubscribe();
    }

    this.valid.complete();
    this.touched.complete();
  }
}
